/**
 * A brand a partner presents to customers, as the API hands it over.
 *
 * Most partners have none, and `undefined` is the answer for them — it means "render
 * the way you always have", not that anything failed.
 */
export interface BrandFontFace {
  family: string;
  src: string;
  weight?: string;
}

export interface BrandTheme {
  primary_color?: string;
  secondary_color?: string;
  primary_text_color?: string;
  dynamic_pricing_color?: string;
  heading_font_family?: string;
  text_font_family?: string;
  button_radius?: string;
  font_faces?: BrandFontFace[];
}

export interface Brand {
  id: string;
  name: string;
  slug: string;
  theme?: BrandTheme | null;
  logoUrl?: string | null;
  faviconUrl?: string | null;
  /** Where the brand's logo links. Unset means the partner's homepage. */
  homepageUrl?: string | null;
  /**
   * Offering directory whose analytics this brand reports to, when it keeps its own
   * property. Unset means the host partner's, which is right for a brand that is only
   * a change of livery.
   */
  analyticsSlug?: string | null;
}

/**
 * A brand as the API actually sends it: snake_case, like every other payload here.
 *
 * Nothing camelizes responses in this app — each data module names the fields it wants
 * one at a time — so a brand read straight off the JSON has `logo_url`, not `logoUrl`,
 * and every camelCase field silently reads `undefined`. This is the one place that
 * conversion happens, so a new field is wrong in one file rather than in three.
 *
 * `theme` is the deliberate exception and passes through untouched: its keys are the
 * custom-property names a partner stylesheet already uses, and `brandCssVariables`
 * reads them in that form.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function toBrand(data: any): Brand | undefined {
  if (!data) {
    return undefined;
  }

  return {
    id: data.id,
    name: data.name,
    slug: data.slug,
    theme: data.theme ?? undefined,
    logoUrl: data.logo_url ?? undefined,
    faviconUrl: data.favicon_url ?? undefined,
    homepageUrl: data.homepage_url ?? undefined,
    analyticsSlug: data.analytics_slug ?? undefined,
  };
}

/**
 * Whether this brand paints its own pages, as opposed to swapping only its logo and
 * favicon. A theme without a primary colour reads the same as no theme at all: a brand
 * that cannot state a palette must keep the partner's stylesheet rather than drop it for
 * the shared base, which would render the partner's page in Flexspace purple.
 */
export function isThemed(brand?: Brand): boolean {
  return Boolean(brand?.theme?.primary_color);
}

/**
 * The brand's palette as the CSS custom properties the shared stylesheets already read.
 *
 * These are the same properties a partner stylesheet sets in its own
 * `/* To customize in each partner *\/` block, which is why a brand can replace that
 * sheet rather than fight it. The `-rgb` pairs and the date-picker trio are derived
 * rather than stored: they are always a restatement of the primary, and a brand that
 * could set them independently could set them inconsistently.
 */
export function brandCssVariables(theme: BrandTheme): Record<string, string> {
  const variables: Record<string, string> = {};

  const primary = theme.primary_color;
  if (primary) {
    const primaryRgb = hexToRgbTriplet(primary);
    variables["--bs-primary"] = primary;
    variables["--mapbox-pin-background-color"] = primary;
    variables["--date-picker-selected"] = primary;
    if (primaryRgb) {
      variables["--bs-primary-rgb"] = primaryRgb;
      variables["--date-picker-selected-edge"] = `rgba(${primaryRgb}, 0.5)`;
      variables["--date-picker-within-selection"] = `rgba(${primaryRgb}, 0.5)`;
    }
  }

  const secondary = theme.secondary_color;
  if (secondary) {
    variables["--bs-secondary"] = secondary;
    const secondaryRgb = hexToRgbTriplet(secondary);
    if (secondaryRgb) {
      variables["--bs-secondary-rgb"] = secondaryRgb;
    }
  }

  // Button text. Bootstrap paints `.btn-primary` white and the legacy button reads
  // `--button-color`, so both need saying: a brand with a pale primary carries dark text
  // and would otherwise be white-on-pale in one place and correct in the other.
  const textOnPrimary = theme.primary_text_color;
  if (textOnPrimary) {
    variables["--brand-primary-text-color"] = textOnPrimary;
    variables["--button-color"] = textOnPrimary;
  }

  // Corner rounding is as much a part of a brand's look as its colour — the difference
  // between a soft button and a square one reads immediately — and it is the one shape
  // property partner sheets reliably set. Buttons only: the sheets that round them leave
  // cards and inputs alone, and widening it would restyle surfaces nobody asked for.
  //
  // Carried under the brand's own name rather than written straight onto Bootstrap's
  // `--bs-btn-border-radius`. That one is declared by `.btn` on the button itself, and a
  // declaration on the element beats an inherited value no matter how important the
  // ancestor's — so the value travels as data here and a rule on `.btn` applies it.
  const buttonRadius = theme.button_radius;
  if (buttonRadius) {
    variables["--brand-button-radius"] = buttonRadius;
  }

  const heading = theme.heading_font_family;
  if (heading) {
    variables["--heading-font-family"] = fontStack(heading);
  }

  const text = theme.text_font_family;
  if (text) {
    const stack = fontStack(text);
    variables["--text-default-font"] = stack;
    variables["--bs-body-font-family"] = stack;
    variables["--bs-font-sans-serif"] = stack;
  }

  return variables;
}

/**
 * The `@font-face` rules a brand's own typeface needs. Returned as text rather than
 * applied, so the caller owns the single `<style>` element and its lifetime.
 *
 * Values are validated server-side before they are stored; this is the one place a brand
 * value cannot be handed to the CSS parser as data, so nothing else is interpolated here.
 */
export function brandFontFaceCss(theme: BrandTheme): string {
  return (theme.font_faces ?? [])
    .filter((face) => face.family && face.src)
    .map((face) =>
      [
        "@font-face {",
        `  font-family: "${face.family}";`,
        `  src: url("${face.src}") format("woff2");`,
        face.weight ? `  font-weight: ${face.weight};` : "",
        "  font-display: swap;",
        "}",
      ]
        .filter(Boolean)
        .join("\n")
    )
    .join("\n\n");
}

/** `#06416c` / `#064` -> `6, 65, 108`. Undefined for anything that is not hex. */
function hexToRgbTriplet(hex: string): string | undefined {
  const digits = hex.replace("#", "");
  const expanded =
    digits.length === 3
      ? digits
          .split("")
          .map((character) => character + character)
          .join("")
      : digits;
  if (!/^[0-9a-f]{6}$/i.test(expanded)) {
    return undefined;
  }
  const channels = expanded.match(/../g);
  return channels?.map((pair) => parseInt(pair, 16)).join(", ");
}

/**
 * A brand names one family; the shared sheets expect a stack. Quoted so a
 * multi-word family survives, and given the same generic fallback the partner
 * stylesheets use so a page still renders if the font never loads.
 */
function fontStack(family: string): string {
  return `"${family}", sans-serif`;
}
